"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

const slides = [
  {
    image: "./BankFinderLogo.svg",
    heading: "Welcome to Banks Finders",
    text: "Discover the best financial solutions for you",
  },
  {
    image: "./Conventionalhero1440x670.webp",
    heading: "Your Trusted Financial Partner",
    text: "Find the right bank that suits your needs",
  },
  {
    image: "./hero-banner-en.webp",
    heading: "Smart Banking Starts Here",
    text: "Explore personalized financial options",
  },
];

export default function CarouselPlugin() {
  const autoplay = useMemo(
    () =>
      Autoplay({
        delay: 5000,
        stopOnInteraction: false,
        stopOnMouseEnter: true,
      }),
    []
  );
  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      align: "start",
    },
    [autoplay]
  );
  const [index, setIndex] = useState(0);

  const syncSelectedIndex = useCallback(() => {
    if (!emblaApi) {
      return;
    }

    setIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) {
      return;
    }

    syncSelectedIndex();
    emblaApi.on("select", syncSelectedIndex);
    emblaApi.on("reInit", syncSelectedIndex);

    return () => {
      emblaApi.off("select", syncSelectedIndex);
      emblaApi.off("reInit", syncSelectedIndex);
    };
  }, [emblaApi, syncSelectedIndex]);

  const prevSlide = useCallback(() => {
    emblaApi?.scrollPrev();
  }, [emblaApi]);

  const nextSlide = useCallback(() => {
    emblaApi?.scrollNext();
  }, [emblaApi]);

  const goToSlide = useCallback(
    (slideIndex: number) => {
      emblaApi?.scrollTo(slideIndex);
    },
    [emblaApi]
  );

  return (
    <div className="relative w-full h-[50vh] sm:h-[70vh] overflow-hidden">
      <div className="h-full overflow-hidden" ref={emblaRef}>
        <div className="flex h-full">
          {slides.map((slide, slideIndex) => (
            <div key={slide.heading} className="basis-full shrink-0 grow-0 h-full">
              <motion.img
                src={slide.image}
                alt={`Slide ${slideIndex + 1}`}
                className="block h-full w-full object-cover"
                initial={{ opacity: 0.85, scale: 1.04 }}
                animate={{
                  opacity: slideIndex === index ? 1 : 0.9,
                  scale: slideIndex === index ? 1 : 1.02,
                }}
                transition={{ duration: 0.8 }}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Caption Overlay */}
      <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 text-center text-white px-4 w-full">
        <motion.div
          key={index}
          className="bg-black/50 p-6 rounded-lg"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-2xl sm:text-4xl font-bold">{slides[index].heading}</h2>
          <p className="text-lg sm:text-xl mt-2">{slides[index].text}</p>
          <a href="#about">
            <Button className="mt-4 bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg">
              Learn More
            </Button>
          </a>
        </motion.div>
      </div>

      {/* Navigation Buttons */}
      <div className="absolute inset-0 flex items-center justify-between px-4 sm:px-6">
        <Button
          variant="outline"
          size="icon"
          onClick={prevSlide}
          className="bg-black/50 text-white hover:bg-black/70 sm:w-12 sm:h-12 w-10 h-10"
        >
          <ChevronLeft size={28} />
        </Button>
        <Button
          variant="outline"
          size="icon"
          onClick={nextSlide}
          className="bg-black/50 text-white hover:bg-black/70 sm:w-12 sm:h-12 w-10 h-10"
        >
          <ChevronRight size={28} />
        </Button>
      </div>

      {/* Indicators */}
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
        {slides.map((_, i) => (
          <Button
            key={i}
            onClick={() => goToSlide(i)}
            variant="ghost"
            className={cn(
              "h-2 w-2 sm:h-3 sm:w-3 rounded-full transition-all",
              i === index ? "bg-white scale-125" : "bg-white/50"
            )}
          />
        ))}
      </div>
    </div>
  );
}
